import type { BootstrapResponse, mintFeeSchema, preparationSchema, randomnessHealthSchema } from "../api/schemas";
import { responseNow } from "../api/response-time";
import { isElysium, elysiumWriteBlock, elysiumMintStateBlock } from "../config/elysium";
import { currentActionFacts, isIsolatedLocalExecution,
  type ActionDecision, type ActionName, type ActionPolicyInput } from "../api/action-capabilities";

export function evaluateActionPolicy(input: ActionPolicyInput): Record<ActionName, ActionDecision> {
  const decide = (action: ActionName): ActionDecision => {
    const permission = input.permissions[action];
    if (permission === null) return { state: "UNKNOWN", reasons: [`${action.toUpperCase()}_PERMISSION_UNKNOWN`] };
    if (!permission) return { state: "BLOCKED", reasons: [`${action.toUpperCase()}_DISABLED`] };
    if (action === "mining") return { state: "ALLOWED", reasons: [] };
    const execution = action === "redeem" ? input.redeemExecution ?? input.execution : input.execution;
    const reasons: string[] = [];
    if (!input.deployment) reasons.push("CONTRACT_NOT_CONFIGURED");
    if (input.writes === false) reasons.push("MAINNET_WRITES_DISABLED");
    if (execution === false) reasons.push("EXECUTION_MODE_DISABLED");
    if (reasons.length) return { state: "BLOCKED", reasons };
    if (input.writes === null || execution === null) {
      return { state: "UNKNOWN", reasons: ["EXECUTION_PERMISSION_UNKNOWN"] };
    }
    // Existing NFTs do not inherit the new-mint or provider-readiness gate.
    return action === "mint" ? input.readiness : { state: "ALLOWED", reasons: [] };
  };
  return { mining: decide("mining"), mint: decide("mint"), finalize: decide("finalize"), redeem: decide("redeem") };
}

export function actionPermissions(bootstrap: BootstrapResponse | null,
  health: ReturnType<typeof randomnessHealthSchema.parse> | null = null,
  mintState: MintDisplayState | null = bootstrap?.mintState ?? null) {
  return evaluateActionPolicy(currentActionFacts(bootstrap, health, mintState));
}

export type MintDisplayState = ReturnType<typeof mintFeeSchema.parse>;

export function mintDisplayCacheStale(state: MintDisplayState | null, now = responseNow(state)): boolean {
  const cache = state?.cache;
  return Boolean(cache && (cache.stale || cache.errorCode !== null || cache.updatedAt === null ||
    cache.expiresAt === null || now === null ||
    Math.min(Date.parse(cache.expiresAt), Date.parse(cache.updatedAt) + cache.ttlSeconds * 1_000) <= now));
}

/** Preparation is an authenticated BE check, not permission to send a transaction. */
export function mintPreparationPermission(bootstrap: BootstrapResponse | null,
  state: MintDisplayState | null = bootstrap?.mintState ?? null): ActionDecision {
  if (!bootstrap) return { state: "UNKNOWN", reasons: ["MINT_STATE_UNAVAILABLE"] };
  if (isElysium(bootstrap)) {
    const reason = elysiumMintStateBlock(bootstrap, state);
    return reason ? { state: "BLOCKED", reasons: [reason] } : { state: "ALLOWED", reasons: [] };
  }
  const v3Block = elysiumWriteBlock(bootstrap);
  if (v3Block) return { state: "BLOCKED", reasons: [v3Block] };
  if (!state && bootstrap.mintState.providerCheckMode === "ON_PREPARE") {
    return { state: "UNKNOWN", reasons: ["MINT_STATE_UNAVAILABLE"] };
  }
  const now = responseNow(state === bootstrap.mintState ? bootstrap : state);
  if (mintDisplayCacheStale(state, now)) return { state: "BLOCKED",
    reasons: [state?.cache?.errorCode ?? (now === null ? "MINT_SERVER_TIME_UNAVAILABLE" : "MINT_DISPLAY_CACHE_STALE")] };
  if (state?.providerCheckMode !== "ON_PREPARE") {
    // Older deployments retain their existing execution gate. Unknown modes grant nothing.
    if (state?.providerCheckMode !== undefined || bootstrap.mintState.providerCheckMode === "ON_PREPARE") {
      return { state: "UNKNOWN", reasons: ["PROVIDER_CHECK_MODE_UNKNOWN"] };
    }
    return actionPermissions(bootstrap).mint;
  }
  const reasons: string[] = [];
  const local = isIsolatedLocalExecution(bootstrap);
  if (!local && !bootstrap.capabilities.publicMintEnabled) reasons.push("MINT_DISABLED");
  if (!local && (bootstrap.capabilities.mintGatewayMode !== "MAINNET_READ_ONLY" ||
    !["VERIFIED_READ_ONLY", "VERIFIED_WRITE_ENABLED"].includes(bootstrap.deployment.projectContractsStatus))) reasons.push("EXECUTION_MODE_DISABLED");
  if (bootstrap.deployment.sourceTokenStatus !== "REGISTERED" || !bootstrap.contracts.collectionAddress ||
    !bootstrap.contracts.mintChainId || bootstrap.contracts.mintChainId !== bootstrap.deployment.executionChainId ||
    state.contractAddress?.toLowerCase() !== bootstrap.contracts.collectionAddress.toLowerCase()) {
    reasons.push("CONTRACT_NOT_CONFIGURED");
  }
  if (!state.available || !state.preparationAvailable) reasons.push(state.reason ?? "MINT_PREPARATION_UNAVAILABLE");
  return reasons.length ? { state: "BLOCKED", reasons } : { state: "ALLOWED", reasons: [] };
}

/** Deployment permission only; a validated sendable preparation is still required. */
export function mintSubmissionPermission(bootstrap: BootstrapResponse | null,
  state: MintDisplayState | null = bootstrap?.mintState ?? null): ActionDecision {
  const v3Block = elysiumWriteBlock(bootstrap);
  if (v3Block) return { state: "BLOCKED", reasons: [v3Block] };
  if (isElysium(bootstrap)) return { state: "ALLOWED", reasons: [] };
  if (!bootstrap || state?.providerCheckMode !== "ON_PREPARE" || isIsolatedLocalExecution(bootstrap)) {
    return actionPermissions(bootstrap).mint;
  }
  const reasons: string[] = [];
  if (!bootstrap.capabilities.publicMintEnabled) reasons.push("MINT_DISABLED");
  if (!bootstrap.capabilities.mainnetWritesEnabled || !bootstrap.deployment.writesEnabled) reasons.push("MAINNET_WRITES_DISABLED");
  if (bootstrap.environment !== "production" || bootstrap.forkTestOnly ||
    bootstrap.capabilities.mintGatewayMode !== "MAINNET_READ_ONLY" ||
    bootstrap.deployment.projectContractsStatus !== "VERIFIED_WRITE_ENABLED") reasons.push("EXECUTION_MODE_DISABLED");
  if (bootstrap.deployment.sourceTokenStatus !== "REGISTERED" || !bootstrap.contracts.collectionAddress ||
    !bootstrap.contracts.mintChainId || bootstrap.contracts.mintChainId !== bootstrap.deployment.executionChainId) {
    reasons.push("CONTRACT_NOT_CONFIGURED");
  }
  return reasons.length ? { state: "BLOCKED", reasons } : { state: "ALLOWED", reasons: [] };
}

export function preparedMintPermission(bootstrap: BootstrapResponse,
  preparation: ReturnType<typeof preparationSchema.parse>,
  state: MintDisplayState = bootstrap.mintState): ActionDecision {
  const permission = mintSubmissionPermission(bootstrap, state);
  if (permission.state !== "ALLOWED") return permission;
  const local = isIsolatedLocalExecution(bootstrap);
  const reasons: string[] = [];
  if (!preparation.call.sendable) reasons.push("MINT_CALL_NOT_SENDABLE");
  if (preparation.call.reason !== null) reasons.push("PREPARATION_REASON_CONFLICT");
  if (local ? !preparation.testOnly : !preparation.mintEnabled || preparation.testOnly) reasons.push("PREPARATION_PERMISSION_DENIED");
  if (preparation.call.mode !== bootstrap.capabilities.mintGatewayMode ||
    preparation.chainId !== bootstrap.contracts.mintChainId ||
    preparation.sourceChainId !== bootstrap.sourceChainId ||
    preparation.call.contractAddress?.toLowerCase() !== bootstrap.contracts.collectionAddress?.toLowerCase()) {
    reasons.push("PREPARATION_DEPLOYMENT_MISMATCH");
  }
  return reasons.length ? { state: "BLOCKED", reasons } : permission;
}

export function walletTransactionExecutionEnabled(capabilities: BootstrapResponse["capabilities"]): boolean {
  // Legacy signature cannot verify environment/deployment. Runtime callers use
  // actionPermissions with the complete bootstrap instead.
  return capabilities.mintGatewayMode === "LOCAL_EVM_TEST_ONLY" && capabilities.localTestOnlyMintTransactionEnabled;
}

/** Catalog + authenticated task authorization grant task eligibility. Global write gates still apply. */
export function taskMintSubmissionPermission(bootstrap: BootstrapResponse | null): ActionDecision {
  if (!bootstrap) return { state: "UNKNOWN", reasons: ["TASK_DEPLOYMENT_UNAVAILABLE"] };
  if (isElysium(bootstrap)) return mintPreparationPermission(bootstrap);
  const v3Block = elysiumWriteBlock(bootstrap);
  if (v3Block) return { state: "BLOCKED", reasons: [v3Block] };
  const facts = currentActionFacts(bootstrap);
  const reasons = [
    ...(!facts.permissions.mint ? ["MINT_DISABLED"] : []),
    ...(!facts.writes ? ["MAINNET_WRITES_DISABLED"] : []),
    ...(!facts.execution ? ["EXECUTION_MODE_DISABLED"] : []),
    ...(!facts.deployment || bootstrap.deployment.sourceTokenStatus !== "REGISTERED" ||
      !bootstrap.contracts.authorizationSignerAddress ? ["TASK_DEPLOYMENT_UNAVAILABLE"] : []),
  ];
  return reasons.length ? { state: "BLOCKED", reasons } : { state: "ALLOWED", reasons: [] };
}

export function mintTransactionExecutionEnabled(capabilities: BootstrapResponse["capabilities"],
  preparationTestOnly: boolean): boolean {
  return walletTransactionExecutionEnabled(capabilities) && preparationTestOnly;
}
